import { MIN_MAP_ZOOM, MAX_MAP_ZOOM } from './constants';

/**
 * Validates individual coordinate values for NaN, finite, and range checks
 */
const validateCoordinates = (lng: number, lat: number): boolean => {
  return (
    !isNaN(lng) && !isNaN(lat) && isFinite(lng) && isFinite(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90
  );
};

/**
 * Sanitizes viewport coordinates and zoom level.
 * Returns null for all values if the coordinate pair is invalid.
 * Clamps zoom to valid range if coordinates are valid.
 */
export const sanitizeViewportSettings = (
  latitude: number | null,
  longitude: number | null,
  zoom: number | null
): { latitude: number | null; longitude: number | null; zoom: number | null } => {
  // If we have both coordinates, validate them as a pair
  if (latitude !== null && longitude !== null) {
    if (validateCoordinates(longitude, latitude)) {
      // Valid coordinates - now handle zoom
      if (zoom !== null && !isNaN(zoom) && isFinite(zoom)) {
        // Clamp zoom to valid range
        return {
          latitude,
          longitude,
          zoom: Math.max(MIN_MAP_ZOOM, Math.min(MAX_MAP_ZOOM, zoom)),
        };
      }
      // Invalid zoom - return coordinates but no zoom
      return { latitude, longitude, zoom: null };
    }
  }

  // If we don't have both coordinates or they're invalid, return all null
  return { latitude: null, longitude: null, zoom: null };
};

/**
 * Validates a LngLat-like object with coordinate range checks
 */
const validateLngLat = (lngLat: { lng: number; lat: number } | null | undefined): boolean => {
  return lngLat !== null && lngLat !== undefined && validateCoordinates(lngLat.lng, lngLat.lat);
};

/**
 * Safe wrapper for map.resize() operations with comprehensive validation
 */
export const safeMapResize = (
  map: {
    resize: () => void;
    getContainer: () => { offsetWidth: number; offsetHeight: number } | null;
    getCenter: () => { lng: number; lat: number } | null;
  } | null
): boolean => {
  if (!map) {
    console.warn('safeMapResize: Map instance is null or undefined');
    return false;
  }

  try {
    // Validate container dimensions
    const container = map.getContainer();
    if (!container || container.offsetWidth <= 0 || container.offsetHeight <= 0) {
      console.warn('safeMapResize: Invalid container dimensions');
      return false;
    }

    // Validate current map center coordinates
    const center = map.getCenter();
    if (!validateLngLat(center)) {
      console.warn('safeMapResize: Invalid center coordinates', center);
      return false;
    }

    map.resize();
    return true;
  } catch (error) {
    console.error('safeMapResize failed:', error);
    return false;
  }
};
